import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AppContext from 'context/Context';
import { TicketMessagesStream } from './StreamComponents';

const CombinedTicketMessagesModal = ({ show, onClose, idTicket, token }) => {
  const context = useContext(AppContext);
  const isDark = Boolean(
    context?.config?.isDark ||
    (typeof document !== 'undefined' && (
      document.documentElement.getAttribute('data-bs-theme') === 'dark' ||
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark')
    ))
  );
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !idTicket || !token) {
      setMessages([]);
      return;
    }

    const fetchCombinedMessages = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/SupportEngine/GetTicketMessages/${idTicket}?pageSize=100`, {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          }
        });

        if (response.ok) {
          const result = await response.json();
          const msgs = result.data || result;
          if (Array.isArray(msgs)) {
            setMessages(msgs.slice().reverse());
          } else {
            setMessages([]);
          }
        } else {
          setError(`Error al cargar los mensajes del ticket #${idTicket}`);
        }
      } catch (err) {
        console.error('Error al obtener mensajes del ticket combinado:', err);
        setError('Error de conexión al cargar mensajes.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCombinedMessages();
  }, [show, idTicket, token]);

  if (!show) return null;

  const handleOpenSourceTicketTab = () => {
    if (!idTicket) return;
    window.open(`/support-engine/ticket/${idTicket}`, '_blank');
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className="se-combined-messages-modal"
      contentClassName={`border-0 shadow-lg overflow-hidden ${isDark ? 'dark__bg-1000 text-light' : 'bg-white text-dark'}`}
    >
      <Modal.Header
        className="py-3 px-4 d-flex align-items-center justify-content-between"
        style={{
          backgroundColor: isDark ? '#16222f' : '#f8f9fa',
          borderBottom: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
              color: isDark ? '#38bdf8' : '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.1rem',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faComments} />
          </div>
          <div>
            <h5 className="modal-title mb-0 fs-0 fw-bold" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
              Mensajes del Ticket Combinado #{idTicket}
            </h5>
            <small style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.8rem' }}>
              Historial de conversación transferido desde el ticket original
            </small>
          </div>
        </div>
        <FalconCloseButton onClick={onClose} variant={isDark ? 'white' : undefined} />
      </Modal.Header>

      <Modal.Body
        className="p-0"
        style={{
          height: '520px',
          maxHeight: '68vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#0b141a' : '#ffffff',
          overflow: 'hidden'
        }}
      >
        {isLoading ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center flex-grow-1 gap-2"
            style={{ backgroundColor: isDark ? '#0b141a' : '#ffffff' }}
          >
            <Spinner animation="border" variant="primary" size="sm" />
            <span style={{ fontSize: '0.85rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              Cargando conversación...
            </span>
          </div>
        ) : error ? (
          <div
            className="d-flex align-items-center justify-content-center flex-grow-1 p-4"
            style={{ backgroundColor: isDark ? '#0b141a' : '#ffffff' }}
          >
            <div
              className="alert mb-0 w-100 text-center"
              style={{
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
                color: isDark ? '#fca5a5' : '#b91c1c',
                border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
                fontSize: '0.88rem'
              }}
            >
              {error}
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4"
            style={{ backgroundColor: isDark ? '#0b141a' : '#ffffff' }}
          >
            <FontAwesomeIcon
              icon={faComments}
              size="2x"
              className="mb-2"
              style={{ color: isDark ? '#334155' : '#cbd5e1' }}
            />
            <p className="mb-0 text-center" style={{ fontSize: '0.9rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              No se encontraron mensajes registrados en el ticket #{idTicket}.
            </p>
          </div>
        ) : (
          <TicketMessagesStream
            messages={messages}
          />
        )}
      </Modal.Body>

      <Modal.Footer
        className="py-2 px-4 d-flex justify-content-between align-items-center"
        style={{
          backgroundColor: isDark ? '#16222f' : '#f8f9fa',
          borderTop: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
        }}
      >
        <div style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b' }}>
          Total de mensajes: <strong style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}>{messages.length}</strong>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenSourceTicketTab}
            title="Abrir este ticket en una nueva pestaña"
            className="d-flex align-items-center gap-1.5 px-3 py-1 fw-semibold"
            style={{
              fontSize: '0.82rem',
              backgroundColor: '#0284c7',
              borderColor: '#0284c7'
            }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" style={{ fontSize: '0.72rem' }} />
            Ir al Ticket
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-sm px-3 shadow-none fw-semibold"
            style={{
              fontSize: '0.82rem',
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              color: isDark ? '#38bdf8' : '#0284c7',
              border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
              transition: 'all 0.15s ease'
            }}
          >
            Cerrar
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

CombinedTicketMessagesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idTicket: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  token: PropTypes.string
};

export default CombinedTicketMessagesModal;
